<template>
  <div class="product-management">
    <!-- 操作工具栏 -->
    <div class="management-toolbar d-flex justify-content-between align-items-center mb-4">
      <div class="toolbar-left">
        <h4 class="mb-0">产品管理</h4>
        <small class="text-muted">管理所有产品信息</small>
      </div>
      <div class="toolbar-right">
        <b-button variant="success" @click="showAddModal">
          <b-icon icon="plus-circle"></b-icon>
          添加产品
        </b-button>
      </div>
    </div>
    
    <!-- 搜索和筛选 -->
    <div class="search-filter-section mb-4">
      <div class="row">
        <div class="col-md-6">
          <b-input-group>
            <b-form-input
              v-model="searchQuery"
              placeholder="搜索产品名称、CAS号或分类..."
              @input="handleSearch"
            ></b-form-input>
            <b-input-group-append>
              <b-button variant="outline-secondary">
                <b-icon icon="search"></b-icon>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
        <div class="col-md-3">
          <b-form-select
            v-model="selectedCategory"
            :options="categoryOptions"
            @change="handleCategoryFilter"
          ></b-form-select>
        </div>
        <div class="col-md-3">
          <b-form-select
            v-model="sortBy"
            :options="sortOptions"
            @change="handleSort"
          ></b-form-select>
        </div>
      </div>
    </div>
    
    <!-- 产品列表 -->
    <div class="products-table-section">
      <b-table
        :items="displayedProducts"
        :fields="tableFields"
        :busy="loading"
        striped
        hover
        responsive
        class="products-table"
      >
        <!-- 产品图片 -->
        <template #cell(image_url)="data">
          <img 
            :src="getImageUrl(data.item.image_url)" 
            :alt="data.item.name"
            class="product-thumbnail"
          >
        </template>
        
        <!-- 产品名称 -->
        <template #cell(name)="data">
          <div class="product-name-cell">
            <strong>{{ data.item.name }}</strong>
          </div>
        </template>
        
        <!-- 分类 -->
        <template #cell(category)="data">
          <b-badge variant="secondary">{{ data.item.category }}</b-badge>
        </template>
        
        <!-- 价格 -->
        <template #cell(price)="data">
          <span class="text-primary font-weight-bold">{{ formatPrice(data.item.price) }}</span>
        </template>
        
        <!-- 操作按钮 -->
        <template #cell(actions)="data">
          <b-button-group size="sm">
            <b-button 
              variant="outline-info" 
              @click="viewProduct(data.item)"
              title="查看详情"
            >
              <b-icon icon="eye"></b-icon>
            </b-button>
            <b-button 
              variant="outline-warning" 
              @click="editProduct(data.item)"
              title="编辑"
            >
              <b-icon icon="pencil"></b-icon>
            </b-button>
            <b-button 
              variant="outline-danger" 
              @click="deleteProduct(data.item)"
              title="删除"
            >
              <b-icon icon="trash"></b-icon>
            </b-button>
          </b-button-group>
        </template>
        
        <!-- 加载状态 -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong class="ml-2">加载中...</strong>
          </div>
        </template>
      </b-table>
      
      <!-- 无数据提示 -->
      <div v-if="!loading && displayedProducts.length === 0" class="no-data text-center py-5">
        <b-icon icon="inbox" font-scale="3" variant="muted" class="mb-3"></b-icon>
        <h5 class="text-muted">暂无产品数据</h5>
        <p class="text-muted">点击上方"添加产品"按钮开始添加产品</p>
      </div>
    </div>
    
    <!-- 添加/编辑产品模态框 -->
    <b-modal
      id="product-form-modal"
      v-model="showFormModal"
      :title="isEditing ? '编辑产品' : '添加产品'"
      size="lg"
      @hidden="resetForm"
    >
      <b-form @submit.prevent="saveProduct">
        <div class="row">
          <div class="col-md-6">
            <b-form-group label-for="product-name" :invalid-feedback="formErrors.name">
              <template #label>
                产品名称 <span class="text-danger">*</span>
              </template>
              <b-form-input
                id="product-name"
                v-model="productForm.name"
                required
                placeholder="请输入产品名称"
                :state="formErrors.name ? false : null"
              ></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label-for="product-cas" :invalid-feedback="formErrors.cas">
              <template #label>
                CAS号
              </template>
              <b-form-input
                id="product-cas"
                v-model="productForm.cas"
                placeholder="请输入CAS号"
                :state="formErrors.cas ? false : null"
              ></b-form-input>
            </b-form-group>
          </div>
        </div>
        
        <div class="row">
          <div class="col-md-6">
            <b-form-group label-for="product-category" :invalid-feedback="formErrors.category">
              <template #label>
                产品分类 <span class="text-danger">*</span>
              </template>
              <b-form-select
                id="product-category"
                v-model="productForm.category"
                :options="categoryOptions"
                required
                :state="formErrors.category ? false : null"
              >
                <template #first>
                  <b-form-select-option value="" disabled>请选择产品分类</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="分子式" label-for="product-formula">
              <b-form-input
                id="product-formula"
                v-model="productForm.formula"
                placeholder="请输入分子式"
              ></b-form-input>
            </b-form-group>
          </div>
        </div>
        
        <b-form-group label="产品描述" label-for="product-description">
          <b-form-textarea
            id="product-description"
            v-model="productForm.description"
            rows="3"
            placeholder="请输入产品描述"
          ></b-form-textarea>
        </b-form-group>
        
        <b-form-group label="品牌/产地" label-for="product-brand-origin">
          <b-form-input
            id="product-brand-origin"
            v-model="productForm.brand_origin"
            placeholder="请输入品牌或产地"
          ></b-form-input>
        </b-form-group>
        
        <div class="row">
          <div class="col-md-4">
            <b-form-group label="纯度" label-for="product-purity">
              <b-form-input
                id="product-purity"
                v-model="productForm.purity"
                placeholder="如：99%"
              ></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group label="包装规格" label-for="product-package">
              <b-form-input
                id="product-package"
                v-model="productForm.package"
                placeholder="如：25kg/桶"
              ></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-4">
            <b-form-group label="参考价格">
              <div class="price-selection mb-2">
                <b-form-radio-group
                  v-model="priceType"
                  :options="priceTypeOptions"
                  name="price-type"
                  size="sm"
                ></b-form-radio-group>
              </div>
              <b-form-input
                v-if="priceType === 'specific'"
                id="product-price"
                v-model="productForm.price"
                placeholder="如：￥100/kg"
              ></b-form-input>
              <b-form-input
                v-else
                value="面议"
                readonly
                class="bg-light"
              ></b-form-input>
            </b-form-group>
          </div>
        </div>
        
        <b-form-group label="主要用途" label-for="product-usage">
          <b-form-input
            id="product-usage"
            v-model="productForm.usage"
            placeholder="请输入主要用途"
          ></b-form-input>
        </b-form-group>
        
        <b-form-group :invalid-feedback="formErrors.images" :state="formErrors.images ? false : null">
          <template #label>
            产品图片 <span class="text-danger">*</span>
          </template>
          <div class="image-upload-section" :class="{ 'is-invalid': formErrors.images }">
            <input
              ref="fileInput"
              type="file"
              multiple
              accept="image/*"
              @change="handleFileSelect"
              style="display: none;"
            />
            
            <!-- 上传进度提示 -->
            <div v-if="uploadingImages" class="upload-progress-container mb-3">
              <div class="d-flex align-items-center justify-content-center p-3 bg-light rounded">
                <b-spinner small class="mr-2"></b-spinner>
                <span class="text-info">正在上传图片，请稍候...</span>
              </div>
            </div>
            
            <!-- 图片预览区域 -->
            <div v-if="imagePreviewUrls.length > 0" class="image-preview-container mb-3">
              <div class="image-preview-grid">
                <div 
                  v-for="(url, index) in imagePreviewUrls" 
                  :key="index"
                  class="image-preview-item"
                >
                  <img :src="url" :alt="`预览图片 ${index + 1}`" class="preview-image">
                  <b-button 
                    size="sm" 
                    variant="danger" 
                    class="remove-image-btn"
                    @click="removeImage(index)"
                    :disabled="uploadingImages"
                  >
                    <b-icon icon="x"></b-icon>
                  </b-button>
                </div>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <small class="text-muted">已选择 {{ imagePreviewUrls.length }} 张图片</small>
                <small v-if="fileSizeInfo" class="text-info font-weight-bold">{{ fileSizeInfo }}</small>
              </div>
            </div>
            
            <!-- 上传区域 -->
            <div 
              class="upload-drop-zone"
              :class="{ 'border-danger': formErrors.images, 'disabled': uploadingImages }"
              @click="!uploadingImages && triggerFileSelect()"
              @dragover.prevent
              @drop.prevent="!uploadingImages && handleFileDrop($event)"
            >
              <div class="upload-content">
                <b-icon icon="cloud-upload" font-scale="2" class="text-muted mb-2"></b-icon>
                <p class="mb-1">{{ uploadingImages ? '正在上传中...' : '点击选择图片或拖拽图片到此处' }}</p>
                <small class="text-muted">支持 JPG、PNG、GIF 格式，可选择多张图片</small>
              </div>
            </div>
          </div>
        </b-form-group>
      </b-form>
      
      <template #modal-footer="{ cancel }">
        <b-button variant="secondary" @click="cancel()">取消</b-button>
        <b-button variant="primary" @click="saveProduct" :disabled="saving">
          <b-spinner v-if="saving" small class="mr-1"></b-spinner>
          {{ saving ? '保存中...' : (isEditing ? '更新' : '添加') }}
        </b-button>
      </template>
    </b-modal>
    
    <!-- 查看产品详情模态框 -->
    <b-modal
      id="product-view-modal"
      v-model="showViewModal"
      :title="viewingProduct?.name"
      size="xl"
      hide-footer
      modal-class="product-detail-modal"
    >
      <div v-if="viewingProduct" class="product-detail-view">
        <div class="row">
          <!-- 产品图片区域 -->
          <div class="col-lg-5">
            <div class="product-image-section">
              <div class="main-product-image mb-3">
                <img 
                  :src="getImageUrl(viewingProduct.currentImage || viewingProduct.image_url)" 
                  :alt="viewingProduct.name"
                  class="img-fluid rounded shadow"
                >
              </div>
              <!-- 如果有多张图片，显示缩略图 -->
              <div v-if="viewingProduct.images && viewingProduct.images.length > 1" class="product-thumbnails"
                   ref="thumbnailContainer"
                   @wheel.prevent="handleThumbnailScroll">
                <div 
                  v-for="(image, index) in viewingProduct.images" 
                  :key="index"
                  class="thumbnail-item"
                  :class="{ active: viewingProduct.currentImageIndex === index }"
                  @click="selectDetailImage(index)"
                  :ref="`thumbnail-${index}`"
                >
                  <img :src="getImageUrl(image)" :alt="`${viewingProduct.name} 图片 ${index + 1}`" class="img-fluid rounded">
                </div>
              </div>
            </div>
          </div>
          
          <!-- 产品信息区域 -->
          <div class="col-lg-7">
            <div class="product-info">
              <h4 class="text-primary mb-3">{{ viewingProduct.name }}</h4>
              <div class="product-meta d-flex align-items-center mb-3">
                <span class="badge badge-secondary mr-3">{{ viewingProduct.category }}</span>
                <span class="text-info font-weight-bold">CAS: {{ viewingProduct.cas }}</span>
              </div>
              
              <div class="info-grid">
                <div class="info-item">
                  <strong>分子式：</strong>{{ viewingProduct.formula }}
                </div>
                <div class="info-item">
                  <strong>品牌/产地：</strong>{{ viewingProduct.brand_origin || '-' }}
                </div>
                <div class="info-item">
                  <strong>纯度：</strong>{{ viewingProduct.purity }}
                </div>
                <div class="info-item">
                  <strong>包装：</strong>{{ viewingProduct.package }}
                </div>
                <div class="info-item">
                  <strong>价格：</strong>{{ formatPrice(viewingProduct.price) }}
                </div>
                <div class="info-item">
                  <strong>用途：</strong>{{ viewingProduct.usage }}
                </div>
              </div>
              
              <div class="mt-3">
                <strong>产品描述：</strong>
                <p class="mt-2">{{ viewingProduct.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'ProductManagement',
  data() {
    return {
      searchQuery: '',
      selectedCategory: 'all',
      sortBy: 'name',
      sortOrder: 'asc',
      showFormModal: false,
      showViewModal: false,
      isEditing: false,
      saving: false,
      uploadingImages: false,
      viewingProduct: null,
      productForm: {
        id: null,
        name: '',
        cas: '',
        category: '',
        description: '',
        brand_origin: '',
        formula: '',
        purity: '',
        package: '',
        price: '',
        usage: '',
        image_url: ''
      },
      formErrors: {
        name: '',
        cas: '',
        category: '',
        images: ''
      },
      priceType: 'negotiable',
      priceTypeOptions: [
        { text: '具体价格', value: 'specific' },
        { text: '面议', value: 'negotiable' }
      ],
      selectedFiles: null,
      imagePreviewUrls: [],
      uploadedImageUrls: [],
      existingImages: [], // 现有图片URLs
      imagesToDelete: [], // 需要删除的图片URLs
      fileSizeInfo: '',
      categories: ['醇类', '溶剂', '芳烃', '环烷烃'],
      tableFields: [
        { key: 'image_url', label: '图片', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'name', label: '产品名称', sortable: true },
        { key: 'category', label: '分类', sortable: true },
        { key: 'specifications', label: '规格' },
        { key: 'price', label: '价格' },
        { key: 'actions', label: '操作', thClass: 'text-center', tdClass: 'text-center' }
      ],
      sortOptions: [
        { value: 'name', text: '按名称排序' },
        { value: 'category', text: '按分类排序' },
        { value: 'price', text: '按价格排序' }
      ]
    }
  },
  computed: {
    ...mapState(['products', 'loading']),
    
    categoryOptions() {
      return [
        { value: '钾类', text: '钾类' },
        { value: '钠类', text: '钠类' },
        { value: '酸类', text: '酸类' },
        { value: '其他', text: '其他' }
      ]
    },

    displayedProducts() {
      let filtered = this.products
      
      // 分类筛选
      if (this.selectedCategory && this.selectedCategory !== 'all') {
        filtered = filtered.filter(product => product.category === this.selectedCategory)
      }
      
      // 搜索筛选
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(product => 
          product.name.toLowerCase().includes(query) ||
          product.category.toLowerCase().includes(query) ||
          (product.description && product.description.toLowerCase().includes(query)) ||
          (product.specifications && product.specifications.toLowerCase().includes(query))
        )
      }
      
      return filtered
    }
  },
  methods: {
    ...mapActions(['fetchProducts', 'addProduct', 'updateProduct']),
    ...mapActions({ removeProduct: 'deleteProduct' }),
    
    // 获取完整的图片URL
    getImageUrl(imageUrl) {
      if (!imageUrl) return ''
      // 如果已经是完整URL，直接返回
      if (imageUrl.startsWith('http')) {
        return imageUrl
      }
      // 如果是相对路径，拼接后端基础URL
      if (!imageUrl) return ''
        return imageUrl
    },
    
    // 格式化价格显示，空值时显示“面议”
    formatPrice(price) {
      if (!price || price === null || price === undefined || price === '') {
        return '面议'
      }
      return price + '元'
    },
    handleSearch() {
      // 搜索逻辑在computed中处理
    },
    handleCategoryFilter() {
      // 分类筛选逻辑在computed中处理
    },
    handleSort() {
      // 排序逻辑在computed中处理
    },
    showAddModal() {
      this.isEditing = false
      this.resetForm()
      this.showFormModal = true
    },
    
    triggerFileSelect() {
      this.$refs.fileInput.click()
    },
    
    handleFileSelect(event) {
      const files = event.target.files
      if (!files || files.length === 0) {
        return
      }
      
      this.addFilesToPreview(Array.from(files))
      // 清空input的值，允许重复选择相同文件
      event.target.value = ''
    },
    
    handleFileDrop(event) {
      const files = event.dataTransfer.files
      if (!files || files.length === 0) {
        return
      }
      
      this.addFilesToPreview(Array.from(files))
    },
    
    addFilesToPreview(files) {
      // 客户端验证配置
      const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
      const MAX_TOTAL_SIZE = 50 * 1024 * 1024 // 50MB
      const MAX_FILE_COUNT = 10 // 最多10张图片
      
      // 验证文件
      const validFiles = []
      const errors = []
      
      // 检查当前已选择的文件数量
      const currentFileCount = this.selectedFiles ? this.selectedFiles.length : 0
      
      files.forEach(file => {
        if (!file.type.startsWith('image/')) {
          errors.push(`${file.name}: 不是有效的图片格式`)
          return
        }
        
        // 检查单个文件大小
        if (file.size > MAX_FILE_SIZE) {
          errors.push(`${file.name}: 文件大小超过5MB限制 (${(file.size / 1024 / 1024).toFixed(2)}MB)`)
          return
        }
        
        // 检查文件数量限制
        if (currentFileCount + validFiles.length >= MAX_FILE_COUNT) {
          errors.push(`最多只能选择${MAX_FILE_COUNT}张图片`)
          return
        }
        
        validFiles.push(file)
      })
      
      // 检查总大小限制
      if (validFiles.length > 0) {
        const currentTotalSize = this.selectedFiles ? 
          this.selectedFiles.reduce((sum, file) => sum + file.size, 0) : 0
        const newTotalSize = validFiles.reduce((sum, file) => sum + file.size, 0)
        const totalSize = currentTotalSize + newTotalSize
        
        if (totalSize > MAX_TOTAL_SIZE) {
          errors.push(`图片总大小超过50MB限制 (当前: ${(totalSize / 1024 / 1024).toFixed(2)}MB)`)
          validFiles.length = 0 // 清空有效文件
        }
      }
      
      // 显示错误信息
      if (errors.length > 0) {
        this.$bvToast.toast(errors.join('\n'), {
          title: '图片验证失败',
          variant: 'warning',
          solid: true,
          autoHideDelay: 8000
        })
      }
      
      // 处理有效文件
      if (validFiles.length > 0) {
        validFiles.forEach(file => {
          const reader = new FileReader()
          reader.onload = (e) => {
            this.imagePreviewUrls.push(e.target.result)
            // 同时保存文件对象用于后续上传
            if (!this.selectedFiles) {
              this.selectedFiles = []
            }
            this.selectedFiles.push(file)
            
            // 更新文件大小显示
            this.updateFileSizeInfo()
          }
          reader.readAsDataURL(file)
        })
        
        // 显示成功添加的文件信息
        if (validFiles.length > 0) {
          const totalSizeMB = validFiles.reduce((sum, file) => sum + file.size, 0) / 1024 / 1024
          this.$bvToast.toast(`成功添加 ${validFiles.length} 张图片 (${totalSizeMB.toFixed(2)}MB)`, {
            title: '添加成功',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000
          })
        }
      }
    },
    
    removeImage(index) {
      const removedUrl = this.imagePreviewUrls[index]
      this.imagePreviewUrls.splice(index, 1)
      
      // 检查删除的是现有图片还是新选择的图片
      const existingImageIndex = this.existingImages.findIndex(url => 
        this.getImageUrl(url) === removedUrl
      )
      
      if (existingImageIndex !== -1) {
        // 删除的是现有图片，添加到删除列表
        const originalUrl = this.existingImages[existingImageIndex]
        this.imagesToDelete.push(originalUrl)
        this.existingImages.splice(existingImageIndex, 1)
      } else {
        // 删除的是新选择的图片，从文件列表中移除
        const selectedFileIndex = this.selectedFiles ? 
          this.selectedFiles.findIndex((file, fileIndex) => {
            // 通过索引计算新文件在预览数组中的位置
            const newFileStartIndex = this.existingImages.length
            return fileIndex === (index - newFileStartIndex)
          }) : -1
        
        if (selectedFileIndex !== -1 && this.selectedFiles) {
          this.selectedFiles.splice(selectedFileIndex, 1)
          if (this.selectedFiles.length === 0) {
            this.selectedFiles = null
          }
          // 更新文件大小显示
          this.updateFileSizeInfo()
        }
      }
    },
    
    updateFileSizeInfo() {
      if (this.selectedFiles && this.selectedFiles.length > 0) {
        const totalSize = this.selectedFiles.reduce((sum, file) => sum + file.size, 0)
        const totalSizeMB = (totalSize / 1024 / 1024).toFixed(2)
        this.fileSizeInfo = `已选择 ${this.selectedFiles.length} 张图片，总大小: ${totalSizeMB}MB`
      } else {
        this.fileSizeInfo = ''
      }
    },
    
    validateForm() {
      this.formErrors = {
        name: '',
        cas: '',
        category: '',
        images: ''
      }
      
      let isValid = true
      
      if (!this.productForm.name.trim()) {
        this.formErrors.name = '请输入产品名称'
        isValid = false
      }
      
      if (!this.productForm.category) {
        this.formErrors.category = '请选择产品分类'
        isValid = false
      }
      
      // 检查是否有图片 - 仅在创建模式下强制要求
      if (!this.isEditing && this.imagePreviewUrls.length === 0) {
        this.formErrors.images = '请至少上传一张产品图片'
        isValid = false
      }
      
      return isValid
    },
    viewProduct(product) {
      // 为产品添加图片切换状态
      const productImages = []
      
      // 处理多张图片
      if (product.images && Array.isArray(product.images) && product.images.length > 0) {
        productImages.push(...product.images)
      } else if (product.image_url) {
        // 如果只有单张图片，也放入数组中
        productImages.push(product.image_url)
      }
      
      this.viewingProduct = {
        ...product,
        images: productImages,
        currentImageIndex: 0,
        currentImage: productImages.length > 0 ? productImages[0] : product.image_url
      }
      this.showViewModal = true
    },
    
    selectDetailImage(index) {
      if (this.viewingProduct && this.viewingProduct.images) {
        this.viewingProduct.currentImageIndex = index
        this.viewingProduct.currentImage = this.viewingProduct.images[index]
        
        // 滚动到选中的缩略图并居中
        this.$nextTick(() => {
          this.scrollThumbnailToCenter(index)
        })
      }
    },
    
    scrollThumbnailToCenter(index) {
      const container = this.$refs.thumbnailContainer
      const thumbnail = this.$refs[`thumbnail-${index}`]?.[0]
      
      if (container && thumbnail) {
        const containerWidth = container.offsetWidth
        const thumbnailLeft = thumbnail.offsetLeft
        const thumbnailWidth = thumbnail.offsetWidth
        
        // 计算居中位置
        const scrollPosition = thumbnailLeft - (containerWidth / 2) + (thumbnailWidth / 2)
        
        // 平滑滚动到计算位置
        container.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        })
      }
    },
    
    handleThumbnailScroll(event) {
      const container = this.$refs.thumbnailContainer
      if (container) {
        // 使用鼠标滚轮进行水平滚动
        const scrollSpeed = 50
        const direction = event.deltaY > 0 ? 1 : -1
        container.scrollLeft += direction * scrollSpeed
      }
    },
    editProduct(product) {
      this.isEditing = true
      this.productForm = { ...product }
      
      // 根据现有价格值设置价格类型
      if (product.price && product.price !== '面议' && product.price !== '' && product.price !== null) {
        this.priceType = 'specific'
      } else {
        this.priceType = 'negotiable'
      }
      
      // 显示现有图片预览，使用完整URL
      this.imagePreviewUrls = []
      this.existingImages = []
      this.imagesToDelete = []
      
      if (product.images && product.images.length > 0) {
        // 如果有多张图片，显示所有图片
        product.images.forEach(imageUrl => {
          const fullUrl = this.getImageUrl(imageUrl)
          this.imagePreviewUrls.push(fullUrl)
          this.existingImages.push(imageUrl) // 保存原始URL用于后续处理
        })
      } else if (product.image_url) {
        // 如果只有单张图片，显示单张图片
        const fullUrl = this.getImageUrl(product.image_url)
        this.imagePreviewUrls.push(fullUrl)
        this.existingImages.push(product.image_url)
      }
      
      // 清空选中的新文件，因为我们显示的是现有图片
      this.selectedFiles = null
      this.fileSizeInfo = ''
      
      this.showFormModal = true
    },
    async deleteProduct(product) {
      if (confirm(`确定要删除产品 "${product.name}" 吗？`)) {
        try {
          // 显示删除进度提示
          this.$bvToast.toast('正在删除产品，请稍候...', {
            title: '删除中',
            variant: 'info',
            solid: true,
            autoHideDelay: 2000
          })
          
          const result = await this.removeProduct(product.id)
          
          if (result.success) {
            // 删除成功，显示成功提示
            this.$bvToast.toast(result.message || '产品删除成功', {
              title: '删除成功',
              variant: 'success',
              solid: true,
              autoHideDelay: 3000
            })
          } else {
            // 删除失败，显示错误提示
            this.$bvToast.toast(result.message || '产品删除失败', {
              title: '删除失败',
              variant: 'danger',
              solid: true,
              autoHideDelay: 5000
            })
          }
        } catch (error) {
          console.error('删除产品时发生错误:', error)
          // 捕获异常，显示错误提示
          this.$bvToast.toast('删除产品时发生错误，请稍后重试', {
            title: '删除失败',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          })
        }
      }
    },
    async saveProduct() {
      // 表单验证
      if (!this.validateForm()) {
        return
      }
      
      this.saving = true
      try {
        // 处理价格
        let finalPrice = null
        if (this.priceType === 'negotiable') {
          finalPrice = '面议'
        } else if (this.productForm.price) {
          finalPrice = this.productForm.price
        }
        
        // 处理图片上传
        let imageUrl = null
        let images = []
        
        // 在编辑模式下处理图片逻辑
        if (this.isEditing) {
          // 保留现有图片（排除被删除的）
          const remainingImages = this.existingImages.filter(url => 
            !this.imagesToDelete.includes(url)
          )
          
          if (this.selectedFiles && this.selectedFiles.length > 0) {
            // 有新图片需要上传，追加到现有图片
          try {
            // 设置上传状态
            this.uploadingImages = true
            
            // 显示上传进度提示
            this.$bvToast.toast(`正在上传 ${this.selectedFiles.length} 张图片，请稍候...`, {
              title: '上传中',
              variant: 'info',
              solid: true,
              autoHideDelay: 2000
            })
            
            if (this.selectedFiles.length === 1) {
              // 单张图片上传，保持向后兼容
              const uploadResult = await this.$api.products.uploadProductImage(this.selectedFiles[0])
              if (uploadResult.url) {
                if (this.isEditing) {
                  // 编辑模式下追加新图片
                  images = [...remainingImages, uploadResult.url]
                  imageUrl = images.length > 0 ? images[0] : uploadResult.url
                } else {
                  imageUrl = uploadResult.url
                  images = [uploadResult.url]
                }
              } else {
                throw new Error(uploadResult.message || '图片上传失败')
              }
            } else {
              // 多张图片上传
              const uploadResult = await this.$api.products.uploadProductImages(this.selectedFiles)
              if (uploadResult.files && uploadResult.files.length > 0) {
                const newImages = uploadResult.files.map(file => file.url)
                // 在编辑模式下，追加新图片到现有图片
                if (this.isEditing) {
                  images = [...remainingImages, ...newImages]
                  imageUrl = images.length > 0 ? images[0] : null
                } else {
                  images = newImages
                  imageUrl = images[0] // 第一张图片作为主图，保持向后兼容
                }
                
                // 如果有部分上传失败的警告
                if (uploadResult.warnings && uploadResult.warnings.length > 0) {
                  this.$bvToast.toast(`部分图片上传失败：${uploadResult.warnings.join(', ')}`, {
                    title: '部分上传失败',
                    variant: 'warning',
                    solid: true,
                    autoHideDelay: 5000
                  })
                }
              } else {
                throw new Error(uploadResult.message || '所有图片上传失败')
              }
            }
            
            // 上传成功提示
            this.$bvToast.toast(`成功上传 ${images.length} 张图片`, {
              title: '上传成功',
              variant: 'success',
              solid: true,
              autoHideDelay: 2000
            })
            
            // 重置上传状态
            this.uploadingImages = false
            
          } catch (uploadError) {
            console.error('图片上传失败:', uploadError)
            
            // 根据错误类型提供不同的提示信息
            let errorMessage = '图片上传失败，请重试'
            if (uploadError.message) {
              if (uploadError.message.includes('timeout')) {
                errorMessage = '图片上传超时，请检查网络连接或减少图片数量后重试'
              } else if (uploadError.message.includes('size') || uploadError.message.includes('大小')) {
                errorMessage = '图片文件过大，请压缩后重试'
              } else if (uploadError.message.includes('format') || uploadError.message.includes('格式')) {
                errorMessage = '图片格式不支持，请使用 PNG、JPG、JPEG、GIF 或 WEBP 格式'
              } else {
                errorMessage = uploadError.message
              }
            }
            
            this.$bvToast.toast(errorMessage, {
              title: '上传失败',
              variant: 'danger',
              solid: true,
              autoHideDelay: 5000
            })
            
            // 重置上传状态
            this.uploadingImages = false
            this.saving = false
            return
          }
          } else {
            // 没有新图片上传，保持现有图片
            images = remainingImages
            imageUrl = remainingImages.length > 0 ? remainingImages[0] : null
          }
        } else if (this.selectedFiles && this.selectedFiles.length > 0) {
          // 创建模式下的图片上传逻辑
          try {
            // 设置上传状态
            this.uploadingImages = true
            
            // 显示上传进度提示
            this.$bvToast.toast(`正在上传 ${this.selectedFiles.length} 张图片，请稍候...`, {
              title: '上传中',
              variant: 'info',
              solid: true,
              autoHideDelay: 2000
            })
            
            if (this.selectedFiles.length === 1) {
              // 单张图片上传，保持向后兼容
              const uploadResult = await this.$api.products.uploadProductImage(this.selectedFiles[0])
              if (uploadResult.url) {
                imageUrl = uploadResult.url
                images = [uploadResult.url]
              } else {
                throw new Error(uploadResult.message || '图片上传失败')
              }
            } else {
              // 多张图片上传
              const uploadResult = await this.$api.products.uploadProductImages(this.selectedFiles)
              if (uploadResult.files && uploadResult.files.length > 0) {
                images = uploadResult.files.map(file => file.url)
                imageUrl = images[0] // 第一张图片作为主图，保持向后兼容
                
                // 如果有部分上传失败的警告
                if (uploadResult.warnings && uploadResult.warnings.length > 0) {
                  this.$bvToast.toast(`部分图片上传失败：${uploadResult.warnings.join(', ')}`, {
                    title: '部分上传失败',
                    variant: 'warning',
                    solid: true,
                    autoHideDelay: 5000
                  })
                }
              } else {
                throw new Error(uploadResult.message || '所有图片上传失败')
              }
            }
            
            // 上传成功提示
            this.$bvToast.toast(`成功上传 ${images.length} 张图片`, {
              title: '上传成功',
              variant: 'success',
              solid: true,
              autoHideDelay: 2000
            })
            
            // 重置上传状态
            this.uploadingImages = false
            
          } catch (uploadError) {
            console.error('图片上传失败:', uploadError)
            
            // 根据错误类型提供不同的提示信息
            let errorMessage = '图片上传失败，请重试'
            if (uploadError.message) {
              if (uploadError.message.includes('timeout')) {
                errorMessage = '图片上传超时，请检查网络连接或减少图片数量后重试'
              } else if (uploadError.message.includes('size') || uploadError.message.includes('大小')) {
                errorMessage = '图片文件过大，请压缩后重试'
              } else if (uploadError.message.includes('format') || uploadError.message.includes('格式')) {
                errorMessage = '图片格式不支持，请使用 PNG、JPG、JPEG、GIF 或 WEBP 格式'
              } else {
                errorMessage = uploadError.message
              }
            }
            
            this.$bvToast.toast(errorMessage, {
              title: '上传失败',
              variant: 'danger',
              solid: true,
              autoHideDelay: 5000
            })
            
            // 重置上传状态
            this.uploadingImages = false
            this.saving = false
            return
          }
        }
        
        // 映射前端字段到后端API字段
        const apiData = {
          name: this.productForm.name,
          cas: this.productForm.cas,
          category: this.productForm.category,
          description: this.productForm.description,
          brand_origin: this.productForm.brand_origin,
          formula: this.productForm.formula,
          purity: this.productForm.purity,
          package: this.productForm.package,
          usage: this.productForm.usage,
          specifications: `分子式: ${this.productForm.formula || '未提供'}, 纯度: ${this.productForm.purity || '未提供'}, 包装: ${this.productForm.package || '未提供'}, 用途: ${this.productForm.usage || '未提供'}`,
          price: finalPrice,
          image_url: imageUrl,
          images: images
        }
        
        // 在编辑模式下，添加需要删除的图片信息
        if (this.isEditing && this.imagesToDelete.length > 0) {
          apiData.imagesToDelete = this.imagesToDelete
        }
        
        let result
        if (this.isEditing) {
          result = await this.updateProduct({ id: this.productForm.id, productData: apiData })
        } else {
          result = await this.addProduct(apiData)
        }
        
        if (result.success) {
          // 成功后自动关闭模态框
          this.showFormModal = false
          this.resetForm()
          
          // 显示成功提示
          this.$bvToast.toast(result.message || (this.isEditing ? '产品更新成功！' : '产品添加成功！'), {
            title: '操作成功',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000
          })
        } else {
          this.$bvToast.toast(result.message || '操作失败', {
            title: '操作失败',
            variant: 'danger',
            solid: true
          })
        }
      } catch (error) {
        console.error('保存产品时出错:', error)
        this.$bvToast.toast('操作失败，请稍后重试', {
          title: '操作失败',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.saving = false
      }
    },
    resetForm() {
      this.productForm = {
        id: null,
        name: '',
        cas: '',
        category: '',
        description: '',
        brand_origin: '',
        formula: '',
        purity: '',
        package: '',
        price: '',
        usage: '',
        image_url: ''
      }
      this.formErrors = {
        name: '',
        cas: '',
        category: '',
        images: ''
      }
      this.priceType = 'negotiable'
      this.selectedFiles = null
      this.imagePreviewUrls = []
      this.uploadedImageUrls = []
      this.existingImages = []
      this.imagesToDelete = []
      this.fileSizeInfo = ''
    }
  },
  async created() {
    await this.fetchProducts()
  }
}
</script>

<style scoped>
.product-management {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.management-toolbar {
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 16px;
}

.search-filter-section {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 6px;
}

.products-table-section {
  max-height: 600px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  background: white;
}

.products-table {
  background: white;
  margin-bottom: 0;
}

/* 自定义滚动条样式 */
.products-table-section::-webkit-scrollbar {
  width: 8px;
}

.products-table-section::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.products-table-section::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.products-table-section::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.product-thumbnail {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
}

.product-name-cell {
  min-width: 200px;
}

.no-data {
  background: #f8f9fa;
  border-radius: 6px;
  margin: 20px 0;
}

.product-detail-view .info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 20px;
}

.product-detail-view .info-item {
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

/* 产品图片轮播样式 */
.product-image-section {
  position: relative;
}

.main-product-image {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
}

.main-product-image img {
  width: 100%;
  height: 300px;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.main-product-image:hover img {
  transform: scale(1.05);
}

.product-thumbnails {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 8px 0;
  /* 显示自定义滚动条 */
  scrollbar-width: thin;
  scrollbar-color: #007bff #f1f3f4;
  scroll-behavior: smooth;
}

/* WebKit浏览器的滚动条样式 */
.product-thumbnails::-webkit-scrollbar {
  height: 6px;
}

.product-thumbnails::-webkit-scrollbar-track {
  background: #f1f3f4;
  border-radius: 3px;
}

.product-thumbnails::-webkit-scrollbar-thumb {
  background: #007bff;
  border-radius: 3px;
}

.product-thumbnails::-webkit-scrollbar-thumb:hover {
  background: #0056b3;
}

.thumbnail-item {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  position: relative;
}

.thumbnail-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 1;
}

.thumbnail-item:hover::before {
  opacity: 1;
}

.thumbnail-item:hover {
  border-color: #007bff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
}

.thumbnail-item.active {
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

.thumbnail-item.active::before {
  opacity: 0;
}

.thumbnail-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-meta {
  margin-bottom: 1rem;
}

.product-meta .badge {
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
}

/* 图片上传样式 */
.image-upload-section {
  border-radius: 8px;
}

.upload-drop-zone {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 32px 16px;
  background: #f8f9fa;
  transition: all 0.3s ease;
  cursor: pointer;
  text-align: center;
}

.upload-drop-zone:hover {
  border-color: #007bff;
  background: #e3f2fd;
}

/* 错误状态样式 */
.upload-drop-zone.border-danger {
  border-color: #dc3545 !important;
  background: #f8d7da;
}

.image-upload-section.is-invalid .upload-drop-zone {
  border-color: #dc3545;
  background: #f8d7da;
}

/* 禁用状态样式 */
.upload-drop-zone.disabled {
  cursor: not-allowed;
  opacity: 0.6;
  background: #e9ecef;
  border-color: #ced4da;
}

.upload-drop-zone.disabled:hover {
  border-color: #ced4da;
  background: #e9ecef;
}

/* 上传进度容器样式 */
.upload-progress-container {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.upload-content p {
  margin: 0;
  color: #495057;
  font-weight: 500;
}

.upload-content small {
  color: #6c757d;
}

.image-preview-container {
  margin-top: 16px;
}

.image-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-bottom: 8px;
}

.image-preview-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #dee2e6;
  background: white;
}

.preview-image {
  width: 100%;
  height: 120px;
  object-fit: cover;
  display: block;
}

.remove-image-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
}

.remove-image-btn:hover {
  opacity: 1;
}

/* 价格选择样式 */
.price-selection .form-check {
  display: inline-block;
  margin-right: 16px;
}

@media (max-width: 768px) {
  .product-detail-view .info-grid {
    grid-template-columns: 1fr;
  }
  
  .main-product-image img {
    height: 250px;
  }
  
  .thumbnail-item {
    width: 50px;
    height: 50px;
  }
  
  .image-preview-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
  
  .preview-image {
    height: 100px;
  }
}
</style>